<script lang="ts">import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { Input } from "@infinitro/input";
import { applyIdentifierInput, finishPasswordResetUi, passwordCredentialsMinLength, startPasswordReset, stateCredentialsStep, stateIdentifier, statePassword, statePasswordConfirm, stateSubmitting } from "../state";
import { CredentialsStep } from "../types";
import { isValidLoginIdentifier, isValidPassword } from "../utils/passwordCredentials";
import Password from "./password.svelte";
$: useFlat = $stateUi.modal?.modalStyle === "flat";
$: flatActionButtonStyle = useFlat ? "height: 48px; background: var(--color-background-500); border-radius: 8px; color: var(--color-text-100);" : "";
$: minLength = passwordCredentialsMinLength();
$: identifierValid = isValidLoginIdentifier($stateIdentifier ?? "");
$: resetReady = isValidPassword($statePassword ?? "", minLength) && $statePassword === $statePasswordConfirm && Boolean($statePasswordConfirm);
let identifierTouched = false;
let autofillLocked = true;
const unlockAutofill = () => {
	autofillLocked = false;
};
const onIdentifierInput = (value) => {
	identifierTouched = true;
	applyIdentifierInput(value);
};
const onIdentifierKeydown = (event) => {
	if (event.key !== "Enter") return;
	event.preventDefault();
	if (identifierValid) startPasswordReset(event);
};
</script>

{#if $stateCredentialsStep === CredentialsStep.Forgot}
	<div class="reset">
		<h2 class="title">{$t(Verbiage.reset_password)}</h2>
		<p class="copy">{$t(Verbiage.reset_password_email_instructions)}</p>
		<label class="field" for="resetIdentifier">
			<span class="label">{$t(Verbiage.username_or_email)} *</span>
			<Input
				type="text"
				name="reset-identifier"
				id="resetIdentifier"
				autocomplete="off"
				readonly={autofillLocked}
				onFocus={unlockAutofill}
				placeholder={$t(Verbiage.enter_your_username_or_email)}
				required
				bind:value={$stateIdentifier}
				onInput={onIdentifierInput}
				onKeydown={onIdentifierKeydown}
				customBorderRadius={useFlat ? '8px' : undefined}
				inputClasses={useFlat ? 'auth-flat-input-el' : ''}
				error={identifierTouched && Boolean($stateIdentifier) && !identifierValid}
			/>
		</label>
		<Button
			text={$t(Verbiage.reset_password)}
			onClick={startPasswordReset}
			disabled={$stateSubmitting || !identifierValid}
			fullWidth
			styleCustom={identifierValid && useFlat
				? 'height: 48px; background: var(--color-primary-500); border-radius: 8px; color: var(--color-text-100);'
				: flatActionButtonStyle}
			id="resetPasswordButton"
			variant={identifierValid ? 'primary' : useFlat ? 'secondary' : 'primary'}
		/>
	</div>
{:else if $stateCredentialsStep === CredentialsStep.Reset}
	<div class="reset">
		<h2 class="title">{$t(Verbiage.set_new_password)}</h2>
		<Password showHint namePrefix="reset-" />
		<Password confirm namePrefix="reset-" />
		<Button
			text={$t(Verbiage.save_password)}
			onClick={finishPasswordResetUi}
			disabled={!resetReady}
			fullWidth
			styleCustom={resetReady && useFlat
				? 'height: 48px; background: var(--color-primary-500); border-radius: 8px; color: var(--color-text-100);'
				: flatActionButtonStyle}
			id="savePasswordButton"
			variant={resetReady ? 'primary' : useFlat ? 'secondary' : 'primary'}
		/>
	</div>
{/if}

<style>
	.reset {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
}
	.title {
		width: 100%;
		text-align: center;
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 600;
		line-height: 140%;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	.copy {
		text-align: center;
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-500);
}
	.field {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 0.5rem;
}
	.label {
		margin-top: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.025em;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	/* Buttons force outline:none on their own focus-visible state; restore keyboard-focus visibility here. */
	:global(#resetPasswordButton:focus-visible),
	:global(#savePasswordButton:focus-visible) {
		outline: 2px solid var(--color-primary-500) !important;
		outline-offset: 2px !important;
	}
</style>
